import { useEffect, useRef, useState } from 'react';
import useAuth from '@/hooks/useAuth';
import useHomeContext from '@/hooks/useHomeContext';
import { getPasskeyCredentials } from '@/services/passkey';
import { NotificationType } from '@/context/types/Notification';
import { passkeySettingsPage } from '@/utils/translations';
import { PASSKEY_SETUP_PROMPT_DISMISSED_LOCAL_STORAGE } from '@/utils/browserStorageNames';
import { isFeatureEnabled } from '@/config/featureFlags.service';
import { Feature } from '@/config/featureFlags.definition';
import {
  PasskeyCreationCancelledError,
  PasskeyUnsupportedError,
  useCreatePasskey
} from '@/features/passkeySettings/hooks/useCreatePasskey';

const DISMISS_DURATION_DAYS = 60;
const DISMISS_DURATION_MS = DISMISS_DURATION_DAYS * 24 * 60 * 60 * 1000;

const dismissedStorageKey = (customerId: string) =>
  `${PASSKEY_SETUP_PROMPT_DISMISSED_LOCAL_STORAGE}:${customerId}`;

const isDismissalActive = (customerId: string): boolean => {
  const dismissedAt = Number(localStorage.getItem(dismissedStorageKey(customerId)));
  return Boolean(dismissedAt) && Date.now() - dismissedAt < DISMISS_DURATION_MS;
};

// Called whenever a customer's passkey list drops to zero (e.g. removing their last one from
// Settings), so the prompt can resurface on their next visit instead of waiting out the rest of
// the 60-day dismissal window - they no longer have a passkey, so "not now" no longer applies.
export const clearPasskeySetupPromptDismissal = (customerId: string) => {
  localStorage.removeItem(dismissedStorageKey(customerId));
};

// Prompts a customer who is on their way into Home (whichever login method got them there) to
// set up a passkey, once per visit, if they don't already have one. Checking passkey existence
// directly - rather than tracking which login method was just used - covers every case: login
// via existing passkey trivially already has one, so the check just never opens the modal for
// that path. Dismissing via "Not now" suppresses the prompt for 60 days, not forever.
export const usePasskeySetupPrompt = () => {
  const { customer } = useAuth()!;
  const { triggerNotification, setError } = useHomeContext()!;
  const { create } = useCreatePasskey();

  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const hasCheckedRef = useRef(false);

  useEffect(() => {
    if (hasCheckedRef.current || !customer?.id) {
      return;
    }
    if (!isFeatureEnabled(Feature.PASSKEY_AUTH)) {
      return;
    }

    hasCheckedRef.current = true;

    if (isDismissalActive(customer.id)) {
      return;
    }

    getPasskeyCredentials()
      .then((response) => {
        if (response.data.length === 0) {
          setIsOpen(true);
        }
      })
      .catch(() => {
        // Passive background check - not worth surfacing an error banner for this.
      });
  }, [customer?.id]);

  const handleNotNow = () => {
    if (customer?.id) {
      localStorage.setItem(dismissedStorageKey(customer.id), String(Date.now()));
    }
    setIsOpen(false);
  };

  const handleCreatePasskey = async () => {
    if (isCreating) {
      return;
    }

    setIsCreating(true);
    try {
      await create();
      triggerNotification(NotificationType.PASSKEY_CREATED);
      setIsOpen(false);
    } catch (err) {
      if (err instanceof PasskeyCreationCancelledError) {
        return;
      }
      if (err instanceof PasskeyUnsupportedError) {
        setError(passkeySettingsPage.errors.unsupported);
        return;
      }
      console.error('Passkey creation failed:', err);
      setError(passkeySettingsPage.errors.createFailed);
    } finally {
      setIsCreating(false);
    }
  };

  return {
    isOpen,
    isCreating,
    handleNotNow,
    handleCreatePasskey
  };
};
